import { AwsRum, AwsRumConfig } from 'aws-rum-web';

interface ErrorMonitoringConfig {
  appId: string;
  identityPoolId: string;
  region?: string;
  environment: string;
}

interface UserContext {
  id: string;
  email?: string;
  username?: string;
}

// Store the AwsRum instance for external access
let awsRumInstance: AwsRum | null = null;

/**
 * Initialize error monitoring with AWS CloudWatch RUM
 * Should be called once at app startup, before any other code runs
 */
export function initErrorMonitoring(config: ErrorMonitoringConfig): void {
  if (!config.appId || !config.identityPoolId) {
    console.warn('CloudWatch RUM App ID or Identity Pool ID not provided, error monitoring disabled');
    return;
  }

  // Only enable in non-development environments
  if (config.environment === 'local' || config.environment === 'development') {
    console.info('CloudWatch RUM disabled in local/development environment');
    return;
  }

  try {
    const rumConfig: AwsRumConfig = {
      sessionSampleRate: config.environment === 'production' ? 0.1 : 1.0,
      identityPoolId: config.identityPoolId,
      endpoint: `https://dataplane.rum.${config.region || 'us-east-2'}.amazonaws.com`,
      telemetries: ['errors', 'performance', 'http'],
      allowCookies: true,
      enableXRay: false,
    };

    awsRumInstance = new AwsRum(
      config.appId,
      '1.0.0',
      config.region || 'us-east-2',
      rumConfig
    );
  } catch (error) {
    console.error('Failed to initialize CloudWatch RUM:', error);
  }
}

/**
 * Capture an error with optional context
 */
export function captureError(
  error: Error,
  context?: Record<string, unknown>
): void {
  if (!awsRumInstance) {
    console.error('Error (RUM not initialized):', error, context);
    return;
  }

  try {
    // AwsRum.recordError accepts an Error object
    awsRumInstance.recordError(error);
  } catch (e) {
    console.error('Failed to record error to CloudWatch RUM:', e);
  }
}

/**
 * Create an error handler for React ErrorBoundary that reports to CloudWatch RUM
 */
export function createErrorHandler() {
  return (error: Error, errorInfo: React.ErrorInfo): void => {
    if (!awsRumInstance) {
      console.error('Error Boundary caught error (RUM not initialized):', error, errorInfo);
      return;
    }

    try {
      awsRumInstance.recordError(error);
    } catch (e) {
      console.error('Failed to record error boundary error to CloudWatch RUM:', e);
    }
  };
}

/**
 * Get the AwsRum instance for direct access if needed
 */
export function getAwsRum(): AwsRum | null {
  return awsRumInstance;
}

/**
 * Set up global error handlers for comprehensive error capture
 * Handles both window.onerror (uncaught errors) and unhandledrejection (promise rejections)
 * Returns a cleanup function to remove the handlers
 */
export function setupGlobalErrorHandlers(): () => void {
  // Handle uncaught JavaScript errors
  window.onerror = (
    message: string | Event,
    source?: string,
    lineno?: number,
    colno?: number,
    error?: Error
  ): boolean => {
    // If we have an Error object, use it directly
    // Otherwise, create one from the message
    const errorToCapture = error || new Error(
      typeof message === 'string'
        ? `${message} at ${source}:${lineno}:${colno}`
        : 'Unknown error'
    );

    captureError(errorToCapture, {
      source,
      lineno,
      colno,
      type: 'uncaught_error',
    });

    // Return false to allow default browser error handling
    return false;
  };

  // Handle unhandled promise rejections
  window.onunhandledrejection = (event: PromiseRejectionEvent): void => {
    const reason = event.reason;

    // Convert reason to Error if it isn't already
    const errorToCapture = reason instanceof Error
      ? reason
      : new Error(
          typeof reason === 'string'
            ? `Unhandled Promise Rejection: ${reason}`
            : `Unhandled Promise Rejection: ${JSON.stringify(reason)}`
        );

    captureError(errorToCapture, {
      type: 'unhandled_rejection',
    });
  };

  // Return cleanup function
  return (): void => {
    window.onerror = null;
    window.onunhandledrejection = null;
  };
}

// Export for testing purposes
export { awsRumInstance };
